import { useElement } from './core/element.js';
import './scroll-view.js';
const name = 's-dialog';
const props = {
    showed: false,
    size: 'standard'
};
const style = `:host{display:inline-block;vertical-align:middle}dialog{top:0;left:0;width:100%;height:100%;background:none;border:none;padding:0;max-width:none;max-height:none;outline:none;color:inherit}dialog::backdrop{background:none}.wrapper{width:100%;height:100%;display:flex;justify-content:center;align-items:center;position:relative;pointer-events:none}.scrim{background:color-mix(in srgb,var(--s-color-scrim, #000000) 80%,transparent);position:absolute;top:0;left:0;width:100%;height:100%;backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);opacity:0;transition:opacity .2s ease-out}dialog.show .scrim{opacity:1}.container,::slotted([slot=custom]){max-width:calc(100% - 48px);max-height:calc(100% - 48px);pointer-events:auto;position:relative;border-radius:28px;display:flex;flex-direction:column;overflow:hidden;transition-timing-function:ease-out;width:425px;box-shadow:var(--s-elevation-level5, 0 10px 14px -6px rgba(0, 0, 0, .2), 0 22px 35px 3px rgba(0, 0, 0, .14), 0 8px 42px 7px rgba(0, 0, 0, .12));background:var(--s-color-surface-container-highest, #ffffff)}:host([size=full]) .container{width:100%;height:100%;border-radius:0;max-width:none;max-height:none}::slotted([slot=headline]){padding:24px 24px 0;font-size:1.5rem;line-height:1.6;font-weight:600;color:var(--s-color-on-surface, #191c1d);flex-shrink:0}.text{user-select:text;-webkit-user-select:text;flex-grow:1}::slotted([slot=text]){margin:16px 24px;line-height:1.6}.action{display:flex;justify-content:flex-end;padding:0 14px;flex-shrink:0}::slotted([slot=action]){min-width:72px;margin:16px 2px;display:inline-flex;align-items:center;padding:0 24px;color:var(--s-color-primary, #00687b);box-sizing:border-box;height:40px;font-size:.875rem;cursor:pointer}`
const template = `<slot name="trigger"></slot><dialog part="popup"><div class="scrim" part="scrim"></div><slot name="custom" class="wrapper" part="wrapper"><div class="container" part="container"><slot name="headline"></slot><s-scroll-view class="text" part="view"><slot></slot><slot name="text"></slot></s-scroll-view><div class="action" part="action"><slot name="action"></slot></div></div></slot></dialog>`
const builder = (options) => {
    let root = document.body;
    const dialog = new Dialog();
    const page = document.body.firstElementChild;
    if (page && page.tagName === 'S-PAGE')
        root = page;
    if (typeof options === 'string') {
        const text = document.createElement('div');
        text.slot = 'text';
        text.textContent = options;
        dialog.appendChild(text);
    }
    else {
        if (options.root)
            root = options.root;
        if (options.headline) {
            const headline = document.createElement('div');
            headline.slot = 'headline';
            headline.textContent = options.headline;
            dialog.appendChild(headline);
        }
        if (options.text) {
            const text = document.createElement('div');
            text.slot = 'text';
            text.textContent = options.text;
            dialog.appendChild(text);
        }
        if (options.view) {
            typeof options.view === 'function' ? options.view(dialog) : dialog.appendChild(options.view);
        }
        for (const item of options.actions ?? []) {
            const action = document.createElement('s-button');
            action.slot = 'action';
            action.type = 'text';
            action.textContent = item.text;
            if (item.click)
                action.addEventListener('click', item.click);
            dialog.appendChild(action);
        }
    }
    dialog.showed = true;
    dialog.addEventListener('closed', () => root.removeChild(dialog));
    root.appendChild(dialog);
    return dialog;
};
class Dialog extends useElement({
    style, template, props, syncProps: true,
    setup(shadowRoot) {
        const dialog = shadowRoot.querySelector('dialog');
        const wrapper = shadowRoot.querySelector('.wrapper');
        shadowRoot.querySelector('slot[name=trigger]').addEventListener('click', () => {
            if (this.showed || !this.dispatchEvent(new CustomEvent('show', { cancelable: true, detail: { source: 'TRIGGER' } })))
                return;
            this.showed = true;
        });
        const onClose = (source) => {
            if (!this.showed || !this.dispatchEvent(new CustomEvent('close', { cancelable: true, detail: { source } })))
                return;
            this.showed = false;
        };
        shadowRoot.querySelector('.scrim').addEventListener('click', () => onClose('SCRIM'));
        shadowRoot.querySelector('slot[name=action]').addEventListener('click', () => onClose('ACTION'));
        const animateOptions = { duration: 200, easing: 'ease-out' };
        const show = () => {
            if (!this.isConnected || dialog.open)
                return;
            dialog.showModal();
            const animation = wrapper.animate([{ transform: 'scale(.9)', opacity: 0 }, { opacity: 1 }], animateOptions);
            dialog.classList.add('show');
            animation.addEventListener('finish', () => this.dispatchEvent(new Event('showed')));
        };
        const close = () => {
            if (!this.isConnected || !dialog.open)
                return;
            const animation = wrapper.animate([{ opacity: 1 }, { transform: 'scale(.9)', opacity: 0 }], animateOptions);
            dialog.classList.remove('show');
            animation.addEventListener('finish', () => {
                dialog.close();
                this.dispatchEvent(new Event('closed'));
            });
        };
        return {
            mounted: () => this.showed && !dialog.open && show(),
            props: {
                showed: (value) => value ? show() : close()
            }
        };
    }
}) {
    static builder = builder;
}
Dialog.define(name);
export { Dialog };
